import React, { useEffect, useState } from 'react';
import DialogLayout from '../../../components/layout/DialogLayout';
import { Divider, FormControl, Grid, InputLabel, MenuItem, Select, Switch, TextField, Tooltip } from '@mui/material';
import { apiCreditToEscrow, apiUpdateDematerialization } from '../../../utils/api/api';
import { useSnackbar } from 'notistack';
import { SNACKBAR_ERROR, SNACKBAR_SUCCESS } from '../../../utils/constants/constants';

const DematDetailsDialog = ({ open, toggleDialog, invOpp, handleSave }) => {
  const [actionLoader, setActionLoader] = useState(false);
  const [dematDetails, setDematDetails] = useState({});
  const [isCreditToEscrow, setIsCreditToEscrow] = useState(0);
  const { enqueueSnackbar } = useSnackbar();

  const handleDematDetailsChange = (event) => {
    const { name, value } = event.target;
    if (name === 'unitsAllotted') {
      const regex = /^\d+(\.\d{0,3})?$/;
      if (regex.test(value) || value === '') {
        setDematDetails({
          ...dematDetails,
          [name]: value,
        });
      }
    } else {
      setDematDetails({
        ...dematDetails,
        [name]: value,
      });
    }
  }
  if (dematDetails.chargesDatetime === "") {
    setDematDetails(prevDetails => ({
      ...prevDetails,
      chargesDatetime: null
    }));
  }
  if (dematDetails.dematCreditDate === "") {
    setDematDetails(prevDetails => ({
      ...prevDetails,
      dematCreditDate: null
    }));
  }

  const saveDematDetails = () => {
    const reqBody = {
      ...dematDetails,
      ioRid: invOpp?.ioRid
    };
    if (dematDetails.isin === "" || dematDetails.isin === null) {
      enqueueSnackbar("Please enter ISIN", SNACKBAR_ERROR);
      return;
    }
    if (dematDetails.unitsAllotted === "" || dematDetails.unitsAllotted === null) {
      enqueueSnackbar("Please enter units allotted", SNACKBAR_ERROR);
      return;
    }
    if (dematDetails.nsdlCdsl === null || dematDetails.nsdlCdsl === "") {
      enqueueSnackbar("Please select NSDL/CDSL", SNACKBAR_ERROR);
      return;
    }
    if (!dematDetails.dematCreditDate) {
      enqueueSnackbar("Please select demat credit date", SNACKBAR_ERROR);
      return;
    }
    if (dematDetails.dematerializationRemark === "" || dematDetails.dematerializationRemark === null) {
      enqueueSnackbar("Please enter dematerialisation remark", SNACKBAR_ERROR);
      return;
    }
    setActionLoader(true);
    apiUpdateDematerialization(reqBody)
      .then((data) => {
        setActionLoader(false);
        enqueueSnackbar('Data Saved', SNACKBAR_SUCCESS);
        handleSave();
      })
      .catch((error) => {
        setActionLoader(false);
        enqueueSnackbar(error, SNACKBAR_ERROR);
      });
  }

  useEffect(() => {

    setDematDetails({})
    if (open) {
      setDematDetails({
        ioRid: invOpp?.ioRid,
        isin: invOpp?.isin,
        unitsAllotted: invOpp?.unitsAllotted ? parseFloat(invOpp.unitsAllotted).toFixed(3) : '',
        aditionalCharges: invOpp?.aditionalCharges,
        nsdlCdsl: invOpp?.nsdlCdsl,
        dematCreditDate: invOpp?.dematCreditDate?.split(' ')[0],
        dematerializationRemark: invOpp?.dematerializationRemark,
        aditionalChargesNarration: invOpp?.aditionalChargesNarration,
        chargesDatetime: invOpp?.chargesDatetime?.split(' ')[0],
        isCreditToEscrow: invOpp?.isMoveToEscrow,
      });
      setIsCreditToEscrow(invOpp?.isMoveToEscrow)
    }
  }, [open]);

  const handleUnitHndlerChange = (event) => {
    const { name, value } = event.target;

    if (name === 'unitsAllotted') {
      const regex = /^\d*\.?\d{0,3}$/;
      if (regex.test(value) || value === '') {
        setDematDetails(prevDetails => ({
          ...prevDetails,
          [name]: value,
        }));
      }
    }
  };

  const creditToEscrow = (value) => {
    apiCreditToEscrow({
      "ioRid": dematDetails.ioRid,
      "isMoveToEscrow": value
    })
      .then((data) => {
        enqueueSnackbar("Request completed successfully", SNACKBAR_SUCCESS)
      }).catch((e) => {
        enqueueSnackbar(e, SNACKBAR_ERROR)
      })
  }


  const handleCreditToEscrow = (value) => {
    creditToEscrow(value);
  };

  const formatUnitsAllotted = (event) => {
    const { name, value } = event.target;
    if (value !== '' && !isNaN(value)) {
      let formattedValue = parseFloat(value).toFixed(3);
      setDematDetails(prevDetails => ({
        ...prevDetails,
        [name]: formattedValue,
      }));

      requestAnimationFrame(() => {
        event.target.selectionStart = event.target.selectionEnd = formattedValue.length;
      });
    }
  };


  return (
    <DialogLayout
      open={open}
      title="Record Demat Details"
      subtitle={invOpp?.invFullName}
      maxWidth={'sm'}
      actionLoader={actionLoader}
      closeAction={toggleDialog}
      negativeAction={
        { label: 'Cancel', onClick: toggleDialog }
      }
      headerRightContent={
        dematDetails?.isin == null ? (
          <Tooltip title="Credit to Escrow" arrow>
            <Switch
              size="medium"
              checked={isCreditToEscrow}
              onChange={() => {
                const updatedValue = isCreditToEscrow === 0 ? 1 : 0;
                setIsCreditToEscrow(updatedValue);
                handleCreditToEscrow(updatedValue);
              }}
              disabled={dematDetails.isin != null}
            />
          </Tooltip>
        ) : null
      }
      positiveAction={{ label: 'Save', onClick: saveDematDetails }}
      disable = {isCreditToEscrow}
    >
      <Grid container rowSpacing={2} columnSpacing={{ xs: 1, sm: 2, md: 2 }} className='py-2'>
        <Grid item xs={6}>
          <TextField
            id="outlined-basic"
            name="isin"
            label="ISIN Code"
            variant="outlined"
            value={dematDetails?.isin ?? ''}
            onChange={(e) => handleDematDetailsChange(e)}
            fullWidth
            disabled={isCreditToEscrow}
          />
        </Grid>
        <Grid item xs={6}>
          <TextField
            id="outlined-basic"
            name="unitsAllotted"
            label="Units Allotted"
            variant="outlined"
            value={dematDetails?.unitsAllotted ?? ''}
            onChange={handleUnitHndlerChange}
            onBlur={formatUnitsAllotted}
            inputProps={{
              inputMode: 'decimal',
              pattern: '^[0-9]+(\\.[0-9]{0,3})?$',
            }}
            fullWidth
            disabled = {isCreditToEscrow}
          />
        </Grid>
        <Grid item xs={6}>
          <FormControl fullWidth>
            <InputLabel id="demo-simple-select-label">NSDL / CDSL</InputLabel>
            <Select
              labelId="demo-simple-select-label"
              id="demo-simple-select"
              name="nsdlCdsl"
              value={dematDetails?.nsdlCdsl || ''}
              label="NSDL / CDSL"
              onChange={(e) => handleDematDetailsChange(e)}
              disabled = {isCreditToEscrow}
            >
              <MenuItem value="NSDL">NSDL</MenuItem>
              <MenuItem value="CDSL">CDSL</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={6}>
          <TextField
            id="outlined-basic"
            type="date"
            name="dematCreditDate"
            label="Demat Credit Date"
            variant="outlined"
            value={dematDetails?.dematCreditDate}
            onChange={(e) => handleDematDetailsChange(e)}
            InputLabelProps={{ shrink: true }}
            fullWidth
            disabled = {isCreditToEscrow}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            id="outlined-basic"
            name="dematerializationRemark"
            label="Remark"
            variant="outlined"
            value={dematDetails?.dematerializationRemark ?? ''}
            onChange={(e) => handleDematDetailsChange(e)}
            fullWidth
            disabled = {isCreditToEscrow}
          />
        </Grid>

        <Grid item xs={12}>
          <Divider />
        </Grid>

        <Grid item xs={12}>
          <span className='section-label'>Charges</span>
        </Grid>

        <Grid item xs={6}>
          <TextField
            id="outlined-basic"
            name="aditionalCharges"
            label="Additional Charges"
            variant="outlined"
            value={dematDetails?.aditionalCharges ?? ''}
            onChange={(e) => handleDematDetailsChange(e)}
            fullWidth
            disabled = {isCreditToEscrow}
          />
        </Grid>
        <Grid item xs={6}>
          <TextField
            id="outlined-basic"
            type="date"
            name="chargesDatetime"
            label="Charges Date"
            variant="outlined"
            value={dematDetails?.chargesDatetime ?? null}
            onChange={(e) => handleDematDetailsChange(e)}
            InputLabelProps={{ shrink: true }}
            fullWidth
            disabled = {isCreditToEscrow}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            id="outlined-basic"
            name="aditionalChargesNarration"
            label="Additional charges remark"
            variant="outlined"
            value={dematDetails?.aditionalChargesNarration ?? ''}
            onChange={(e) => handleDematDetailsChange(e)}
            fullWidth
            disabled = {isCreditToEscrow}
          />
        </Grid>
      </Grid>
    </DialogLayout>
  );
}

export default DematDetailsDialog;